import {
  GuestInvoiceDraftPayload,
  GuestPendingIntent,
} from './guestInvoiceDraftStorage'
import {
  SavedUserProfile,
  shouldConfirmZeroTotalSend,
  shouldShowProfileOverwritePrompt,
} from './guestHydrationOrchestrator'

export type SessionReadyPrompt = {
  invoiceId: string
  pendingIntent: GuestPendingIntent
}

export type EditPagePostHydrationInput = {
  guestDraft: GuestInvoiceDraftPayload | null
  savedProfile: SavedUserProfile
  sessionReadyPrompt: SessionReadyPrompt | null
  profileOverwriteResolved: boolean
  readyPromptDismissed: boolean
}

export type EditPagePostHydrationOutcome =
  | { modal: 'profileOverwrite' }
  | {
      modal: 'guestInvoiceReady'
      pendingIntent: GuestPendingIntent
      primaryAction: GuestPendingIntent
    }
  | { modal: 'zeroTotalSend'; sendEmailPath: string }
  | { modal: 'none'; clearGuestDraft: boolean }

export type ReadyPromptUserAction = 'keepEditing' | 'viewPdf' | 'send'

export type PlanReadyPromptActionInput = {
  guestDraft: GuestInvoiceDraftPayload | null
  invoiceId: string
  action: ReadyPromptUserAction
}

const isProfileOverwritePhaseComplete = (
  guestDraft: GuestInvoiceDraftPayload | null,
  savedProfile: SavedUserProfile,
  profileOverwriteResolved: boolean,
): boolean =>
  !guestDraft
  || !shouldShowProfileOverwritePrompt(savedProfile, guestDraft)
  || profileOverwriteResolved

const isReadyPromptPhaseComplete = (
  sessionReadyPrompt: SessionReadyPrompt | null,
  readyPromptDismissed: boolean,
): boolean => !sessionReadyPrompt || readyPromptDismissed

const shouldClearGuestDraft = (input: EditPagePostHydrationInput): boolean =>
  input.guestDraft != null
  && isProfileOverwritePhaseComplete(
    input.guestDraft,
    input.savedProfile,
    input.profileOverwriteResolved,
  )
  && isReadyPromptPhaseComplete(input.sessionReadyPrompt, input.readyPromptDismissed)

export const buildSendEmailPath = (invoiceId: string): string =>
  `/invoices/${invoiceId}/send-email`

export const planEditPagePostHydration = (
  input: EditPagePostHydrationInput,
): EditPagePostHydrationOutcome => {
  const {
    guestDraft,
    savedProfile,
    sessionReadyPrompt,
    profileOverwriteResolved,
    readyPromptDismissed,
  } = input

  if (
    sessionReadyPrompt
    && guestDraft
    && shouldShowProfileOverwritePrompt(savedProfile, guestDraft)
    && !profileOverwriteResolved
  ) {
    return { modal: 'profileOverwrite' }
  }

  if (sessionReadyPrompt && !readyPromptDismissed) {
    const { pendingIntent } = sessionReadyPrompt
    return {
      modal: 'guestInvoiceReady',
      pendingIntent,
      primaryAction: pendingIntent,
    }
  }

  return {
    modal: 'none',
    clearGuestDraft: shouldClearGuestDraft(input),
  }
}

const shouldConfirmZeroTotalForSend = (
  guestDraft: GuestInvoiceDraftPayload | null,
): boolean => {
  if (!guestDraft) return false
  return shouldConfirmZeroTotalSend({ ...guestDraft, pendingIntent: 'send' })
}

export const planReadyPromptAction = (
  input: PlanReadyPromptActionInput,
): EditPagePostHydrationOutcome => {
  const { guestDraft, invoiceId, action } = input

  if (action === 'send' && shouldConfirmZeroTotalForSend(guestDraft)) {
    return {
      modal: 'zeroTotalSend',
      sendEmailPath: buildSendEmailPath(invoiceId),
    }
  }

  return { modal: 'none', clearGuestDraft: true }
}

export const planZeroTotalSendConfirmed = (
  _invoiceId: string,
): EditPagePostHydrationOutcome => ({
  modal: 'none',
  clearGuestDraft: true,
})
